// Image optimization utilities

/**
 * Optimize image URL for faster loading
 * Adds query parameters for optimization based on image source
 */
export const optimizeImageUrl = (url, options = {}) => {
  if (!url || typeof url !== 'string') return url;
  
  const {
    width = 900,
    quality = 75,
    format = 'auto'
  } = options;

  try {
    const parsed = new URL(url);
    
    // Optimize Unsplash images
    if (parsed.hostname.includes('images.unsplash.com')) {
      parsed.searchParams.set('w', width.toString());
      parsed.searchParams.set('q', quality.toString());
      parsed.searchParams.set('auto', format);
      parsed.searchParams.set('fit', 'crop');
      return parsed.toString();
    }
    
    // Optimize Firebase Storage images
    if (parsed.hostname.includes('firebasestorage.googleapis.com')) {
      // Firebase Storage URLs can be optimized by adding transformations
      // For now, we'll return as-is but could add image transformations
      return url;
    }
    
    // For other URLs, return as-is
    return url;
  } catch (error) {
    return url;
  }
};

/**
 * Preload an image
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Preload multiple images
 */
export const preloadImages = async (urls) => {
  const promises = urls.map(url => preloadImage(url).catch(err => {
    console.warn(`Failed to preload image: ${url}`, err);
    return null;
  }));
  return Promise.all(promises);
};

/**
 * Get optimized image URL for different contexts
 */
export const getOptimizedImageUrl = (url, context = 'default') => {
  const contexts = {
    thumbnail: { width: 300, quality: 70 },
    card: { width: 600, quality: 75 },
    product: { width: 900, quality: 80 },
    hero: { width: 1920, quality: 85 },
    default: { width: 900, quality: 75 }
  };
  
  const options = contexts[context] || contexts.default;
  return optimizeImageUrl(url, options);
};



